import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import FullscreenFlow from "../../FullscreenFlow.tsx";
import FullscreenLayout from "../../FullscreenLayout.tsx";
import ameelio from "../../ameelio.svg";
import ResetScroll from "../../lib/ResetScroll.tsx";
import StepperWrapper from "../../lib/StepperWrapper.tsx";
import getWorkflowState from "../../lib/getWorkflowState.ts";
import { Step, Steps } from "./steps";
import TestAudioStep from "./steps/TestAudioStep.tsx";
import TestMicrophoneStep from "./steps/TestMicrophoneStep.tsx";
import TestVideoStep from "./steps/TestVideoStep.tsx";

type Props = {
  standalone?: boolean;
  backTo?: string;
  parentName?: string;
};

type ContainerProps = {
  standalone?: boolean;
  backTo: string;
  breadcrumbs: [string, string];
  children: React.ReactNode;
};

function FullscreenContainer({
  standalone,
  backTo,
  breadcrumbs,
  children,
}: ContainerProps) {
  if (standalone) {
    return (
      <FullscreenLayout
        corner={<img src={ameelio} alt="Ameelio" style={{ width: 100 }} />}
      >
        {children}
      </FullscreenLayout>
    );
  }
  return (
    <FullscreenFlow backTo={backTo} breadcrumbs={breadcrumbs}>
      {children}
    </FullscreenFlow>
  );
}

export default function TestSoundVideoFlow({
  standalone,
  backTo,
  parentName,
}: Props) {
  const { t } = useTranslation();

  const [currentStep, setCurrentStep] = useState<Step>("testVideo");
  const navigate = useNavigate();

  const location = useLocation();
  const state = getWorkflowState(location) ?? {
    fromPath: backTo || "/",
    fromName: parentName || t("Home"),
  };

  const steps = Steps.slice();
  return (
    <FullscreenContainer
      standalone={standalone}
      backTo={state.fromPath}
      breadcrumbs={[state.fromName, t("Test sound and video")]}
    >
      <ResetScroll key={currentStep} />
      {currentStep === "testVideo" && (
        <TestVideoStep
          stepper={({ disabled }) =>
            StepperWrapper({
              buttonType: "next",
              onBack: () => {
                navigate(state.fromPath);
              },
              loading: false,
              disabled,
              steps,
              step: currentStep,
            })
          }
          onSubmit={() => {
            setCurrentStep("testMicrophone");
          }}
        />
      )}
      {currentStep === "testMicrophone" && (
        <TestMicrophoneStep
          stepper={({ disabled }) =>
            StepperWrapper({
              buttonType: "next",
              onBack: () => setCurrentStep("testVideo"),
              loading: false,
              disabled,
              steps,
              step: currentStep,
            })
          }
          onSubmit={() => {
            setCurrentStep("testAudio");
          }}
        />
      )}
      {currentStep === "testAudio" && (
        <TestAudioStep
          stepper={({ disabled }) =>
            StepperWrapper({
              buttonType: "next",
              onBack: () => setCurrentStep("testMicrophone"),
              loading: false,
              disabled,
              steps,
              step: currentStep,
            })
          }
          onSubmit={() => {
            navigate(state.fromPath);
          }}
        />
      )}
    </FullscreenContainer>
  );
}
